import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../../components/PageConfigProvider/constants';
import type {
  PrimaryFilterConfig,
  TabContent,
} from '../../components/PageConfigProvider/page-reducer';
import {
  type Page,
  PageType,
  RevelioPageId,
} from '../../components/PageConfigProvider/types';
import { FilterId } from '../../filters/types/filters';

const companyCompositionSnapshot: TabContent['items'] = [
  {
    position: { x: 0, width: 12, y: 0, height: 8 },
    id: 'company-composition-v2-snapshot-headcount',
    title: 'Headcount',
    info: {
      text: 'Our estimate of the total workforce. These counts include subsidiaries and may include both formal employees and contingent workers, depending on your preference settings. More information on the methodologies used for this metric can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#sampling-weights',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['headcount'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 12, width: 12, y: 0, height: 8 },
    id: 'company-composition-v2-snapshot-growth-rate',
    title: 'Growth Rate',
    info: {
      text: 'The growth rate is the percent change in the total workforce. It is the difference between the hiring rate and attrition rate.',
    },
    plot: {
      defaultMetricOptions: ['growth_rate'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 24, width: 12, y: 0, height: 8 },
    id: 'company-composition-v2-snapshot-hiring-rate',
    title: 'Hiring Rate',
    info: {
      text: 'The annual rate at which workers join, relative to the total headcount. It is the 12-month moving sum of inflows divided by the 12-month moving average of headcount.',
    },
    plot: {
      defaultMetricOptions: ['hiring_rate'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 36, width: 12, y: 0, height: 8 },
    id: 'company-composition-v2-snapshot-attrition-rate',
    title: 'Attrition Rate',
    info: {
      text: 'The annual rate at which workers leave, relative to the total headcount. It is the 12-month moving sum of outflows divided by the 12-month moving average of headcount.',
    },
    plot: {
      defaultMetricOptions: ['attrition_rate'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 0, width: 24, y: 8, height: 14 },
    id: 'company-composition-v2-snapshot-role',
    title: 'Role',
    info: {
      text: 'The composition of workers by occupation. More information on the job representation and taxonomy can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#jobs-taxonomy',
        },
      ],
    },
    plot: {
      secondaryDimension: 'role',
      defaultMetricOptions: ['headcount'],
      chartType: 'stacked-bar',
      adaptive: true,
      treemap: { sizeTransform: 'sqrt' },
    },
  },
  {
    position: { x: 24, width: 24, y: 8, height: 14 },
    id: 'company-composition-v2-snapshot-geography',
    title: 'Geography',
    info: {
      text: 'The composition of workers by geography.',
    },
    plot: {
      secondaryDimension: 'geography',
      defaultMetricOptions: ['headcount'],
      chartType: 'stacked-bar',
      adaptive: true,
      treemap: { sizeTransform: 'sqrt' },
    },
  },
  {
    position: { x: 0, width: 24, y: 22, height: 14 },
    id: 'company-composition-v2-snapshot-skill',
    title: 'Skill',
    info: {
      text: 'The composition of workers by skill. More information on the skill model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#combining-skill-clusters',
        },
      ],
    },
    plot: {
      secondaryDimension: 'skills',
      defaultMetricOptions: ['headcount'],
      chartType: 'stacked-bar',
      adaptive: true,
      hideOther: true,
      treemap: { sizeTransform: 'sqrt' },
    },
  },
  {
    position: { x: 24, width: 24, y: 22, height: 14 },
    id: 'company-composition-v2-snapshot-seniority',
    title: 'Seniority',
    info: {
      text: 'The composition of workers by seniority. More information on the seniority model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#seniority',
        },
      ],
    },
    plot: {
      secondaryDimension: 'seniority',
      defaultMetricOptions: ['headcount'],
      chartType: 'stacked-bar',
      adaptive: true,
      treemap: { sizeTransform: 'sqrt' },
    },
  },
];

const companyCompositionOvertime: TabContent['items'] = [
  {
    position: { x: 0, width: 12, y: 0, height: 8 },
    id: 'company-composition-v2-overtime-headcount',
    title: 'Headcount',
    info: {
      text: 'Our estimate of the total workforce. These counts include subsidiaries and may include both formal employees and contingent workers, depending on your preference settings. More information on the methodologies used for this metric can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#sampling-weights',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['headcount'],
      chartType: 'line',
    },
  },
  {
    position: { x: 12, width: 12, y: 0, height: 8 },
    id: 'company-composition-v2-overtime-growth-rate',
    title: 'Growth Rate',
    info: {
      text: 'The growth rate is the percent change in the total workforce. It is the difference between the hiring rate and attrition rate.',
    },
    plot: {
      defaultMetricOptions: ['growth_rate'],
      chartType: 'line',
    },
  },
  {
    position: { x: 24, width: 12, y: 0, height: 8 },
    id: 'company-composition-v2-overtime-hiring-rate',
    title: 'Hiring Rate',
    info: {
      text: 'The annual rate at which workers join, relative to the total headcount. It is the 12-month moving sum of inflows divided by the 12-month moving average of headcount.',
    },
    plot: {
      defaultMetricOptions: ['hiring_rate'],
      chartType: 'line',
    },
  },
  {
    position: { x: 36, width: 12, y: 0, height: 8 },
    id: 'company-composition-v2-overtime-attrition-rate',
    title: 'Attrition Rate',
    info: {
      text: 'The annual rate at which workers leave, relative to the total headcount. It is the 12-month moving sum of outflows divided by the 12-month moving average of headcount.',
    },
    plot: {
      defaultMetricOptions: ['attrition_rate'],
      chartType: 'line',
    },
  },
  {
    position: { x: 0, width: 24, y: 8, height: 14 },
    id: 'company-composition-v2-overtime-role',
    title: 'Role',
    info: {
      text: 'The composition of workers by occupation. More information on the job representation and taxonomy can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#jobs-taxonomy',
        },
      ],
    },
    plot: {
      secondaryDimension: 'role',
      defaultMetricOptions: ['headcount'],
      chartType: 'line',
      shareOfSegment: true,
    },
  },
  {
    position: { x: 24, width: 24, y: 8, height: 14 },
    id: 'company-composition-v2-overtime-geography',
    title: 'Geography',
    info: {
      text: 'The composition of workers by geography.',
    },
    plot: {
      secondaryDimension: 'geography',
      defaultMetricOptions: ['headcount'],
      chartType: 'line',
      shareOfSegment: true,
    },
  },
  {
    position: { x: 0, width: 24, y: 22, height: 14 },
    id: 'company-composition-v2-overtime-skill',
    title: 'Skill',
    info: {
      text: 'The composition of workers by skill. More information on the skill model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#combining-skill-clusters',
        },
      ],
    },
    plot: {
      secondaryDimension: 'skills',
      defaultMetricOptions: ['headcount'],
      chartType: 'line',
      shareOfSegment: true,
    },
  },
  {
    position: { x: 24, width: 24, y: 22, height: 14 },
    id: 'company-composition-v2-overtime-seniority',
    title: 'Seniority',
    info: {
      text: 'The composition of workers by seniority. More information on the seniority model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#seniority',
        },
      ],
    },
    plot: {
      secondaryDimension: 'seniority',
      defaultMetricOptions: ['headcount'],
      chartType: 'line',
      shareOfSegment: true,
    },
  },
];

const sharedConfig: Pick<TabContent, 'grid'> & {
  primaryFilter: PrimaryFilterConfig;
} = {
  primaryFilter: {
    id: FilterId.COMPANY,
  },
  grid: { rows: DEFAULT_GRID_ROWS, columns: DEFAULT_GRID_COLUMNS },
};

const companySnapshotSecondaryFilters: TabContent['secondaryFilters'] = [
  FilterId.ACTIVITY,
  FilterId.CONTINGENT_WORKERS,
  FilterId.DATA_ADJUSTMENTS,
  FilterId.EDUCATION,
  FilterId.ETHNICITY,
  FilterId.GENDER,
  FilterId.GEOGRAPHY,
  FilterId.KEYWORDS,
  FilterId.ROLE,
  FilterId.SENIORITY,
  FilterId.CUSTOM_SKILL,
  FilterId.SNAPSHOT_DATE,
  FilterId.TITLE,
].map((id) => ({ id }));

const companyCompositionSnapshotTab: TabContent = {
  id: 'company-composition-v2-snapshot',
  items: companyCompositionSnapshot,
  ...sharedConfig,
  secondaryFilters: companySnapshotSecondaryFilters,
};

const companyCompositionOvertimeTab: TabContent = {
  id: 'company-composition-v2-overtime',
  items: companyCompositionOvertime,
  ...sharedConfig,
  primaryFilter: { ...sharedConfig.primaryFilter, tagColors: true },
  secondaryFilters: [
    FilterId.ACTIVITY,
    FilterId.CONTINGENT_WORKERS,
    FilterId.DATA_ADJUSTMENTS,
    FilterId.DATE_RANGE,
    FilterId.EDUCATION,
    FilterId.ETHNICITY,
    FilterId.GENDER,
    FilterId.GEOGRAPHY,
    FilterId.KEYWORDS,
    FilterId.ROLE,
    FilterId.SENIORITY,
    FilterId.CUSTOM_SKILL,
    FilterId.TITLE,
  ].map((id) => ({ id })),
};

export const companyComposition: Page = {
  type: PageType.REVELIO_PUBLIC,
  id: RevelioPageId.COMPANY_COMPOSITION,
  revelioPageId: RevelioPageId.COMPANY_COMPOSITION,
  name: 'Overview',
  owner: { id: 'revelio-labs', username: 'revelio-labs' },
  tabs: [
    {
      id: 'company-composition-v2-snapshot',
      label: 'Snapshot',
      isActive: true,
      tabContent: companyCompositionSnapshotTab,
    },
    {
      id: 'company-composition-v2-overtime',
      label: 'Over Time',
      isActive: false,
      tabContent: companyCompositionOvertimeTab,
    },
  ],
};
